import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Skeleton } from "@mui/material";

import styles from "./requests.module.css";

import chatPlaceholder from "../../../../assets/images/chatPlaceholder.svg";

import * as matchAction from "../../../../actions/match";
import { getProfileImageUrl } from "../../../../utils/serverUtils";

export default function Requests() {
  const { type } = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  const observer = useRef();

  const placeholderText =
    type === "received"
      ? "New requests to match will appear here"
      : "Sent requests will appear here";

  const fetchRequests = useCallback(
    async (pageNumber) => {
      if (!type || (!hasMore && pageNumber !== 1)) return;

      setLoading(true);
      try {
        const response = await matchAction.getRequestReceived(type, pageNumber);

        if (response) {
          setRequests((prev) => {
            const combined = [...prev, ...(response || [])];
            const uniqueMap = new Map();
            combined.forEach((req) =>
              uniqueMap.set(req.participant_id || req.sender_id || req.conversationId, req)
            );
            return Array.from(uniqueMap.values());
          });

          if (response.length < 20) setHasMore(false);
        } else {
          setHasMore(false);
        }
      } catch (error) {
        console.error("Fetch failed:", error);
      } finally {
        setLoading(false);
      }
    },
    [type, hasMore]
  );

  useEffect(() => {
    if (!type) return;
    setPage(1);
    setRequests([]);
    setHasMore(true);
    fetchRequests(1);
  }, [type]);

  useEffect(() => {
    if (page === 1) return;
    fetchRequests(page);
  }, [page, fetchRequests]);

  const lastElementRef = useCallback(
    (node) => {
      if (loading || !hasMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const getDateDifference = (date) => {
    const today = dayjs();
    const messageDate = dayjs(date);
    const diff = today.diff(messageDate, "day");
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    return messageDate.format("DD/MM/YYYY");
  };

  const renderRequestItem = (request, index) => (
    <div
      key={request.participant_id || index}
      ref={index === requests.length - 1 ? lastElementRef : null}
      className={`${styles.message} request`}
      onClick={() =>
        navigate(`/user/${request?.sender_name?.split(" ")[0]}/${request?.sender_id}`)
      }
    >
      <img
        src={getProfileImageUrl(request)}
        alt="profile"
        width={50}
        height={50}
        style={{ objectFit: "contain" }}
      />
      <div>
        <p>{request?.sender_name || request?.name}</p>
        <p>{request?.last_message_body}</p>
      </div>
      <div style={{ flexGrow: 1 }}></div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <p style={{ fontSize: "smaller", color: "#6c6c6c", fontWeight: 500 }}>
          {getDateDifference(request.created_at)}
        </p>
      </div>
    </div>
  );

  const renderSentItem = (request, index) => (
    <div
      key={request.conversationId || index}
      ref={index === requests.length - 1 ? lastElementRef : null}
      className={`${styles.message} request`}
    >
      <svg width="32" height="32" viewBox="0 0 32 32">
        <path
          d="M4.39 7.06C4.63 6.82 4.97 6.67 5.33 6.67H26.67C27.03 6.67 27.37 6.82 27.61 7.06M4.39 7.06C4.15 7.3 4 7.63 4 8V24C4 24.74 4.6 25.33 5.33 25.33H26.67C27.4 25.33 28 24.74 28 24V8C28 7.63 27.85 7.3 27.61 7.06M4.39 7.06L14.11 16.78C15.16 17.82 16.84 17.82 17.89 16.78L27.61 7.06"
          stroke="#6C6C6C"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div>
        <p style={{ fontWeight: 500, color: "#6c6c6c" }}>
          You requested to chat with {request.name}
        </p>
      </div>
      <div style={{ flexGrow: 1 }}></div>
      <p style={{ fontSize: "smaller", color: "#6c6c6c", fontWeight: 500 }}>
        {getDateDifference(request.created_at)}
      </p>
    </div>
  );

  return (
    <>
      <ul className={styles.nav}>
        <li
          onClick={(e) => {
            e.preventDefault();
            navigate("/user/chat/requests/received")
          }}

          className={type === "received" ? styles.active : ""}
        >
          Received <div className={styles.indicator}></div>
        </li>
        <li
          onClick={(e) => {
            e.preventDefault();
            navigate("/user/chat/requests/sent")
          }}
          className={type === "sent" ? styles.active : ""}
        >
          Sent <div className={styles.indicator}></div>
        </li>
      </ul>

      {!loading && requests.length === 0 && (
        <div className={styles.chatPlaceholder}>
          <img src={chatPlaceholder} alt="No requests" />
          <p>{placeholderText}</p>
        </div>
      )}

      <div className={styles.messagesContainer}>
        {loading &&
          Array.from({ length: 6 }).map((_, index) => (
            <Skeleton
              key={index}
              animation="pulse"
              variant="rounded"
              width="100%"
              height={50}
              sx={{ borderRadius: "12px" }}
            />
          ))}

        {!loading &&
          (type === "received"
            ? requests.map((req, idx) => renderRequestItem(req, idx))
            : requests.map((req, idx) => renderSentItem(req, idx)))}
      </div>
    </>
  );
}

